import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Icon from "./Icon";
import ZestComponent from "./ZestComponent";
import { slugify } from "./slugify";
import type { IconLibrary } from "./types";

// React rebuild of the ZEST v2 Figma component set Zest::Tab

export interface TabDropdownOption {
  name: string;
  badgeText?: string;
  content?: React.ReactNode;
  src?: string;
  frameId?: string;
}

export interface TabProps {
  id?: string;
  panelId?: string;
  name: string;
  icon?: string | null;
  iconLibrary?: IconLibrary;
  badgeText?: string | null;
  active?: boolean;
  href?: string | null;
  dropdownOptions?: TabDropdownOption[] | null;
  selectedOptionIndex?: number;
  dropdownOpen?: boolean;
  onSelect?: () => void;
  onSelectOption?: (index: number) => void;
  onRequestClose?: () => void;
}

interface FlyoutPosition {
  top: number;
  left: number;
  width: number;
}

type FocusKey = "ArrowDown" | "ArrowUp" | "ArrowLeft" | "ArrowRight" | "Home" | "End";

const Badge = ({ text }: { text: string }): JSX.Element => (
  <span className="inline-block text-center whitespace-nowrap rounded px-2 py-1 text-sm bg-brand text-negative font-mono">
    {text}
  </span>
);

const badgeTextFor = ({
  dropdownOptions,
  selectedOptionIndex,
  badgeText,
}: Pick<TabProps, "dropdownOptions" | "badgeText"> & { selectedOptionIndex: number }): string | null | undefined =>
  dropdownOptions?.length ? dropdownOptions[selectedOptionIndex]?.badgeText : badgeText;

const labelFor = ({
  name,
  dropdownOptions,
  selectedOptionIndex,
}: Pick<TabProps, "name" | "dropdownOptions"> & { selectedOptionIndex: number }): string =>
  dropdownOptions?.length ? `${name}: ${dropdownOptions[selectedOptionIndex]?.name}` : name;

// The tab row scrolls horizontally (`overflow-auto`), which clips any
// absolutely-positioned descendant — including this flyout. Portaling it to
// `document.body` and positioning it from the trigger's own bounding rect
// keeps it visible regardless of where it sits in the scrollable row.
//
// Runs in useLayoutEffect (not useEffect) so the flyout has its real position
// before paint instead of visibly jumping from top:0 on open.
const useFlyoutPosition = ({
  triggerRef,
  open,
}: {
  triggerRef: React.RefObject<HTMLElement>;
  open: boolean;
}): FlyoutPosition | null => {
  const [position, setPosition] = useState<FlyoutPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = (): void => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, triggerRef]);

  return position;
};

const useCloseOnOutsideInteraction = ({
  open,
  refs,
  onClose,
}: {
  open: boolean;
  refs: React.RefObject<HTMLElement>[];
  onClose: () => void;
}): void => {
  // Callers naturally pass a fresh array literal for `refs` and a fresh arrow
  // for `onClose` on every render. Listing either as a dependency would tear
  // down and re-register both document listeners on every single render, so
  // they're read through a ref instead and the effect depends only on `open`.
  const latest = useRef({ refs, onClose });

  useEffect(() => {
    latest.current = { refs, onClose };
  });

  useEffect(() => {
    if (!open) return undefined;

    const isInside = (target: EventTarget | null): boolean =>
      latest.current.refs.some((ref) => ref.current?.contains(target as Node) ?? false);

    const handlePointerDown = (event: MouseEvent): void => {
      if (!isInside(event.target)) latest.current.onClose();
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") latest.current.onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
};

// Returns focus to the trigger whenever the dropdown transitions from open to
// closed — covers escape, outside-click, and option-select, since all three
// funnel through the same `dropdownOpen` prop change. Without this, focus
// silently drops to <body> once the flyout unmounts.
const useReturnFocusOnClose = ({
  open,
  triggerRef,
}: {
  open: boolean;
  triggerRef: React.RefObject<HTMLElement>;
}): void => {
  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (wasOpenRef.current && !open) triggerRef.current?.focus();
    wasOpenRef.current = open;
  }, [open, triggerRef]);
};

const focusSiblingByRole = ({
  container,
  role,
  key,
}: {
  container: HTMLElement | null;
  role: string;
  key: FocusKey;
}): void => {
  const items = Array.from(container?.querySelectorAll<HTMLElement>(`[role="${role}"]`) ?? []);
  if (!items.length) return;

  const currentIndex = items.indexOf(document.activeElement as HTMLElement);
  const movingForward = key === "ArrowDown" || key === "ArrowRight";
  let nextIndex: number;

  if (key === "Home") nextIndex = 0;
  else if (key === "End") nextIndex = items.length - 1;
  else if (currentIndex === -1) nextIndex = movingForward ? 0 : items.length - 1;
  else nextIndex = (currentIndex + (movingForward ? 1 : -1) + items.length) % items.length;

  items[nextIndex]?.focus();
};

// Listbox options are plain focus targets, not native <button>s: a button
// inside a listbox brings its own tab stop, and role="option" overrides its
// button semantics anyway. Enter/Space activation, which a button gave for
// free, therefore has to be wired up explicitly.
const activateOnEnterOrSpace =
  (activate: () => void) =>
  (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    activate();
  };

const isFocusKey = (key: string): key is FocusKey =>
  ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(key);

const Tab = ({
  id,
  panelId,
  name,
  icon = null,
  iconLibrary = "standard",
  badgeText = null,
  active = false,
  href = null,
  dropdownOptions = null,
  selectedOptionIndex = 0,
  dropdownOpen = false,
  onSelect,
  onSelectOption,
  onRequestClose,
}: TabProps): JSX.Element => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const flyoutRef = useRef<HTMLElement | null>(null);
  const hasDropdown = Boolean(dropdownOptions?.length);
  const tabId = id ?? `zest-tab-${slugify(name)}`;
  const listboxId = `${tabId}-listbox`;
  const label = labelFor({ name, dropdownOptions, selectedOptionIndex });
  const resolvedBadgeText = badgeTextFor({ dropdownOptions, selectedOptionIndex, badgeText });
  const flyoutPosition = useFlyoutPosition({ triggerRef, open: dropdownOpen });
  // aria-controls takes a space-separated list of IDREFs, so the trigger can
  // point at both its panel and — only while it exists in the DOM — the open
  // flyout. Referencing an absent id would be invalid, hence the open check.
  const controlledIds = [panelId, hasDropdown && dropdownOpen ? listboxId : null].filter(Boolean).join(" ");

  useCloseOnOutsideInteraction({
    open: dropdownOpen,
    refs: [triggerRef, flyoutRef],
    onClose: () => onRequestClose?.(),
  });
  useReturnFocusOnClose({ open: dropdownOpen, triggerRef });

  // ArrowDown both opens a closed dropdown and, once open, moves focus into
  // it — the listbox-popup pattern. Opening routes through `onSelect` because
  // the open/closed state lives with the parent (Tabs toggles it there).
  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (!hasDropdown || event.key !== "ArrowDown") return;

    event.preventDefault();

    if (!dropdownOpen) {
      onSelect?.();
      return;
    }

    focusSiblingByRole({ container: flyoutRef.current, role: "option", key: "Home" });
  };

  const handleListboxKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (!isFocusKey(event.key) || event.key === "ArrowLeft" || event.key === "ArrowRight") return;

    event.preventDefault();
    focusSiblingByRole({ container: flyoutRef.current, role: "option", key: event.key });
  };

  // Link tabs (`href` set) navigate like a normal GET link — react_ujs/Turbo
  // handles the page transition — while still updating local active/dropdown
  // state on click so the UI reacts immediately.
  const TriggerTag: React.ElementType = href ? "a" : "button";

  return (
    <ZestComponent name="tab" className="shrink-0 snap-start" variants={{ active }}>
      <TriggerTag
        ref={(node: HTMLElement | null) => {
          triggerRef.current = node;
        }}
        id={tabId}
        type={href ? undefined : "button"}
        href={href || undefined}
        role="tab"
        aria-selected={active}
        aria-controls={controlledIds || undefined}
        aria-haspopup={hasDropdown ? "listbox" : undefined}
        aria-expanded={hasDropdown ? dropdownOpen : undefined}
        tabIndex={active ? 0 : -1}
        onClick={onSelect}
        onKeyDown={handleTriggerKeyDown}
        className={`flex gap-1 items-center pb-2 border-b-2 font-bold uppercase text-base leading-6 whitespace-nowrap ${
          active ? "text-focus border-brand" : "text-secondary border-transparent"
        }`}
      >
        {icon && (
          <span className="flex h-4 w-4 items-center justify-center">
            <Icon icon={icon} library={iconLibrary} />
          </span>
        )}
        {resolvedBadgeText && <Badge text={resolvedBadgeText} />}
        {label}
        {hasDropdown && (
          <span className={`flex h-4 w-4 items-center justify-center  ${dropdownOpen ? "rotate-180" : ""}`}>
            <Icon icon="chevron-down" />
          </span>
        )}
      </TriggerTag>

      {hasDropdown &&
        dropdownOpen &&
        flyoutPosition &&
        dropdownOptions &&
        createPortal(
          <div
            ref={flyoutRef as React.RefObject<HTMLDivElement>}
            id={listboxId}
            role="listbox"
            aria-label={name}
            style={{ top: flyoutPosition.top, left: flyoutPosition.left, width: flyoutPosition.width }}
            className="fixed z-20 flex flex-col gap-2 bg-white py-2"
            onKeyDown={handleListboxKeyDown}
          >
            {dropdownOptions.map((option, index) => (
              <div
                key={option.name}
                role="option"
                tabIndex={-1}
                aria-selected={index === selectedOptionIndex}
                onClick={() => onSelectOption?.(index)}
                onKeyDown={activateOnEnterOrSpace(() => onSelectOption?.(index))}
                className="flex gap-1 items-center font-bold uppercase text-base leading-6 whitespace-nowrap text-secondary cursor-pointer"
              >
                {option.badgeText && <Badge text={option.badgeText} />}
                {option.name}
              </div>
            ))}
          </div>,
          document.body
        )}
    </ZestComponent>
  );
};

export default Tab;
